import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoArrowBack } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import { useBackHandler } from "./BackHandlerContext";
import Haptic from "./Haptic";
import "./pageHeader.css";

const PAGE_TITLE_KEYS = {
  "/": "home",
  "/BookingPage": "booking",
  "/Products": "products",
  "/CreateUser": "createUser",
  "/LogIn": "logIn",
  "/login": "logIn",
  "/forgot-password": "forgotPassword",
  "/MyCart": "myCart",
  "/mycart": "myCart",
  "/Profile": "profile",
  "/profile": "profile",
  "/Settings": "settings",
  "/settings": "settings",
  "/tickets": "tickets",
  "/Orders": "myOrders",
  "/orders": "myOrders",
  "/support": "support",
  "/SUPPORT": "support",
  "/personvern": "privacy",
  "/coupons": "couponsRewards",
  "/klippekort-redeem": "redeemKlippekort",
  "/paymentreturn": "paymentConfirmation",
  "/PaymentReturn": "paymentConfirmation",
};

const NO_BACK_ROUTES = ["/", "/Products", "/Profile", "/LogIn", "/login", "/coupons"];

export default function PageHeader() {
  const { t } = useTranslation('pageHeader');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { handler: customBackHandler } = useBackHandler();
  const [refreshing, setRefreshing] = useState(false);
  const titleKey = PAGE_TITLE_KEYS[pathname] ?? null;
  const title = titleKey ? t(titleKey) : null;
  // react-router's browser history stamps state.idx with the position in the
  // in-app history stack - 0 means this is the first entry (a direct visit,
  // fresh tab, or reload), so there's no real in-app page to go back to yet.
  // Without this, landing straight on e.g. /login or /coupons still showed a
  // back button that would navigate(-1) into whatever was open before this
  // site, not anywhere inside the app.
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  const showBack = !NO_BACK_ROUTES.includes(pathname) && canGoBack;

  if (!title) return null;

  const handleBack = () => {
    if (navigator.vibrate) navigator.vibrate(20);
    // A page (e.g. klippekort-redeem's archive/detail sub-views) can register a
    // handler to step back through its own local view state instead of leaving the route.
    if (customBackHandler) { customBackHandler(); return; }
    navigate(-1);
  };

  // No full page reload (blank screen, and every reload re-inits Supabase and
  // rotates its one-time refresh token): App.js remounts the open page instead,
  // so it fetches its data again.
  const handleRefresh = () => {
    if (navigator.vibrate) navigator.vibrate(20);
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 600);
    window.dispatchEvent(new Event('pws:refresh'));
  };

  return (
    <header className="page-header">
      <div className="page-header__inner">
        {showBack && (
          <Haptic onTap={handleBack} inline wrapperStyle={{ position: 'absolute', left: 0 }}>
            <button className="page-header__back" aria-label={t('goBack')}>
              <IoArrowBack size={24} />
            </button>
          </Haptic>
        )}
        <h1 className="page-header__title">{title}</h1>
        <Haptic onTap={handleRefresh} inline wrapperStyle={{ position: 'absolute', right: 0 }}>
          <button className={`page-header__refresh${refreshing ? ' page-header__refresh--spinning' : ''}`} aria-label={t('refresh')}>
            <FiRefreshCw size={19} strokeWidth={2.5} />
          </button>
        </Haptic>
      </div>
    </header>
  );
}
